import type {
    BulkEnquiryItem,
    BulkEnquiryItemType,
} from './bulk-enquiry-state';

interface Noun {
    one: string;
    many: string;
}

const NOUNS: Record<BulkEnquiryItemType, Noun> = {
    product: { one: 'product', many: 'products' },
    project: { one: 'project', many: 'projects' },
    professional: { one: 'professional', many: 'professionals' },
};

/**
 * "item" is our word, not the reader's — every label names the real thing
 * (products / projects / professionals) instead. A list can only be mixed by
 * selecting across browse pages, which is rare, so the mixed wording just drops
 * the noun rather than reaching for a vague one.
 */
export const selectionType = (
    items: BulkEnquiryItem[],
): BulkEnquiryItemType | null => {
    const first = items[0]?.itemType;
    if (!first) return null;
    return items.every((item) => item.itemType === first) ? first : null;
};

/** "3 projects", "1 product" — or, for a mixed list, "3 selected". */
export const countLabel = (items: BulkEnquiryItem[]): string => {
    const total = items.length;
    const type = selectionType(items);
    if (!type) return `${total} selected`;

    const noun = NOUNS[type];
    return `${total} ${total === 1 ? noun.one : noun.many}`;
};

/**
 * The header line: what you picked, and how many professionals it reaches. A
 * list of professionals reaches exactly itself, so it doesn't restate the count.
 */
export const selectionSummary = (
    items: BulkEnquiryItem[],
    supplierCount: number,
): string => {
    const label = countLabel(items);
    if (selectionType(items) === 'professional') return label;
    return supplierCount > 1
        ? `${label} · ${supplierCount} professionals`
        : label;
};

/**
 * Spells out the fan-out before sending — one enquiry per professional, each
 * carrying only their own things. Null when there's only one recipient.
 */
export const fanOutNote = (
    items: BulkEnquiryItem[],
    supplierCount: number,
): string | null => {
    if (supplierCount <= 1) return null;

    const type = selectionType(items);
    const tail = `${supplierCount} enquiries in total.`;

    if (type === 'professional') {
        return `We'll send a separate enquiry to each of them — ${tail}`;
    }

    const theirs = type
        ? `their ${NOUNS[type].many}`
        : 'what you picked from them';
    return `Each professional gets their own enquiry with only ${theirs} — ${tail}`;
};

/**
 * The subject the default enquiry message is built around ("Hi, I'm interested
 * in {subject}…").
 *
 * Every professional gets the same message but only their own slice of the list,
 * so once more than one is involved the subject must not quote a count — "these
 * 6 products" baffles a supplier who was sent one of them.
 */
export const messageSubject = (
    items: BulkEnquiryItem[],
    supplierCount: number,
): string => {
    if (items.length === 1) return items[0]?.title ?? 'this';

    const type = selectionType(items);
    if (!type) return "what I've selected";

    if (supplierCount === 1) return `these ${countLabel(items)}`;

    return type === 'professional'
        ? 'your services'
        : `the ${NOUNS[type].many} I've selected`;
};
